import { Component } from "react";

/**
 * Error boundary component for graceful error handling.
 * Catches errors in child components and displays fallback UI.
 * Logs errors in development for debugging.
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Error boundary component for graceful error handling.
 * Catches errors in child components and displays fallback UI.
 * Logs errors in development for debugging.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log errors in development only
    if (import.meta.env.DEV) {
      console.error("Error boundary caught:", error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
